/**
 * Classify mutation hook for the Work Log Review page — task 159d.
 *
 * Wraps `POST /api/v1/orgs/:orgId/work-log-review/classify` (the 159b
 * endpoint). Live mode posts the body verbatim and returns the response.
 * Fixture mode short-circuits with a synthetic response shape so the
 * Classify modal renders its success and conflict-warning paths without a
 * backend.
 *
 * Per the existing fixture-mode convention (mockup mutations are best-
 * effort displays, not stateful), the no-op does not modify any fixture
 * data. The page's success banner surfaces verbiage; the work-log-review
 * query is invalidated so the page re-renders, but in fixture mode the
 * fixture events are unchanged so the user will see the same rows.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import type { ClassificationSource } from '@/types/workLogReview';

export type ClassifyRuleField = 'app' | 'title' | 'url' | 'site' | 'ip' | 'document';

export interface ClassifyScopeFilter {
  from?: string;
  to?: string;
  employeeIds?: readonly string[];
  businessUnitId?: string;
  classificationSource?: readonly ClassificationSource[];
  classifiedByRuleId?: string;
  projectId?: string;
  clientId?: string;
}

export interface ClassifyRule {
  name: string;
  priority: number;
  condition: {
    type: 'pattern' | 'regex' | 'cidr';
    field: ClassifyRuleField;
    value: string;
  };
  enabled?: boolean;
  businessUnitId?: string | null;
}

export type ClassifyScope =
  | { kind: 'group'; key: Record<string, string | null>; filter: ClassifyScopeFilter }
  | { kind: 'events'; eventIds: readonly string[] };

export interface ClassifyWorkLogBody {
  scope: ClassifyScope;
  projectId: string;
  rule?: ClassifyRule;
}

export interface ClassifyConflictReport {
  eventsCaughtByExistingRule: number;
  sampleRuleNames: string[];
}

export interface ClassifyWorkLogResult {
  appliedManualOverrides: number;
  conflictReport: ClassifyConflictReport | null;
  createdRule: { id: string; name: string; priority: number } | null;
}

// Fixture-mode-only knob: when the proposed rule's value matches one of
// these strings (case-insensitive substring), the hook synthesises a
// non-zero conflict report so the modal's conflict-warning path renders
// without a real backend. The seed list lines up with `RULES` in
// `web/frontend/src/data/rules.ts` — adding to either should be reflected
// here.
const FIXTURE_CONFLICT_SEEDS: ReadonlyArray<{ value: string; ruleName: string }> = [
  { value: 'outlook', ruleName: 'Meridian Outlook' },
];

function fixtureConflictFor(rule: ClassifyRule | undefined): ClassifyConflictReport | null {
  if (!rule) return null;
  const v = rule.condition.value.toLowerCase();
  for (const seed of FIXTURE_CONFLICT_SEEDS) {
    if (v.includes(seed.value)) {
      return {
        eventsCaughtByExistingRule: 3,
        sampleRuleNames: [seed.ruleName],
      };
    }
  }
  return { eventsCaughtByExistingRule: 0, sampleRuleNames: [] };
}

function fixtureAppliedCount(scope: ClassifyScope): number {
  if (scope.kind === 'events') return scope.eventIds.length;
  // Group scope — the modal does not track the precise count; mirror the
  // backend's "we recompute on the server" fact by returning a sensible
  // placeholder. The success banner shows "Classified N events" and 1 is
  // the safest non-misleading default for fixture mode (the user can see
  // exactly which group they classified — the count is informational).
  return 1;
}

export function useClassifyWorkLog(): UseMutationResult<
  ClassifyWorkLogResult,
  Error,
  ClassifyWorkLogBody
> {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation<ClassifyWorkLogResult, Error, ClassifyWorkLogBody>({
    mutationFn: async (body) => {
      if (mockupMode) {
        // Fixture-mode synthetic response. Logged for dev visibility; the
        // page does not actually mutate fixture state.
        // eslint-disable-next-line no-console -- mockup-mode dev visibility; live mode never reaches this branch
        console.info('[mockup] useClassifyWorkLog', body);
        return {
          appliedManualOverrides: fixtureAppliedCount(body.scope),
          conflictReport: fixtureConflictFor(body.rule),
          createdRule: body.rule
            ? { id: 'mockup-rule', name: body.rule.name, priority: body.rule.priority }
            : null,
        };
      }
      const token = await getToken();
      return apiFetch<ClassifyWorkLogResult>(
        `/api/v1/orgs/${currentOrg.id}/work-log-review/classify`,
        token,
        { method: 'POST', body: JSON.stringify(body) },
      );
    },
    onSuccess: () => {
      // Invalidate every variant of the work-log-review query — filter,
      // group-by, sort, and pagination all factor into the query key, so
      // a strict-equality invalidation would miss most cached entries.
      void qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'workLogReview' && q.queryKey[1] === currentOrg.id,
      });
      // The classify endpoint may have created a rule and writes to
      // classified_event; the rules list is the surface that will reflect
      // the new rule.
      void qc.invalidateQueries({ queryKey: ['rules', currentOrg.id] });
    },
  });
}
